import React, { useState } from 'react';
import { CheckIcon, PlusIcon } from 'lucide-react';
import type { Product, VariantSelection } from '../../types/product';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { getProduct } from '../../utils/products';
interface Props {
  product: Product;
  selection: VariantSelection;
  mainPrice: number;
  mainImage: string;
}
export function FrequentlyBought({
  product,
  selection,
  mainPrice,
  mainImage
}: Props) {
  const {
    addItem
  } = useCart();
  const extras = (product.bundleIds ?? []).map(getProduct).filter((p): p is Product => !!p);
  const [checked, setChecked] = useState<string[]>(extras.map(p => p.id));
  if (extras.length === 0) return null;
  const selectedExtras = extras.filter(p => checked.includes(p.id));
  const total = mainPrice + selectedExtras.reduce((s, p) => s + p.price, 0);
  const addAll = () => {
    addItem(product, selection, 1, selectedExtras.length === 0);
    selectedExtras.forEach((p, i) => addItem(p, undefined, 1, i === selectedExtras.length - 1));
  };
  return <section aria-labelledby="fbt-heading" className="rounded-2xl bg-surface p-5 sm:p-6" data-id="element-285" data-mp="3n6yfarbfukj2">
      <h2 id="fbt-heading" className="text-lg font-bold tracking-tight text-ink" data-id="element-286" data-mp="3s6ubi4dw97o2">
        Frequently bought together
      </h2>
      <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-center" data-id="element-287" data-mp="z8cip24dtdj">
        <div className="flex flex-1 items-center gap-3 overflow-x-auto" data-id="element-288" data-mp="2zif7se07qbs1">
          <div className="w-28 shrink-0" data-id="element-289" data-mp="34gzxvklr3f2r">
            <img src={mainImage} alt="" className="aspect-square w-full rounded-lg bg-white object-cover" data-id="element-290" data-mp="34i1kmmc4pq0b" />
            <p className="mt-2 line-clamp-2 text-xs font-medium text-ink" data-id="element-291" data-mp="39fb83wfyjrli">This item</p>
            <p className="text-sm font-bold text-ink" data-id="element-292" data-mp="3e7uwzpk3i95m">{formatPrice(mainPrice)}</p>
          </div>
          {extras.map(p => {
          const on = checked.includes(p.id);
          return <React.Fragment key={p.id} data-id="element-293" data-mp="2lpgwtjbaeb3d">
                <PlusIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" data-id="element-294" data-mp="2loe5jaa1y6mx" />
                <label className={`w-28 shrink-0 cursor-pointer transition-opacity duration-150 ${on ? '' : 'opacity-50'}`} data-id="element-295" data-mp="2dnzup1qanjzo">
                  <span className="relative block" data-id="element-296" data-mp="2nfmcjgx50hbe">
                    <img src={p.image} alt="" className="aspect-square w-full rounded-lg bg-white object-cover" data-id="element-297" data-mp="2neaz0pdw3v73" />
                    <input type="checkbox" checked={on} onChange={() => setChecked(c => on ? c.filter(x => x !== p.id) : [...c, p.id])} className="peer sr-only" data-id="element-298" data-mp="2ihb337ar88tp" />
                    <span className={`absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded border peer-focus-visible:ring-2 peer-focus-visible:ring-ink/40 ${on ? 'border-ink bg-ink' : 'border-ink/30 bg-white'}`} aria-hidden="true" data-id="element-299" data-mp="24q61w0rqn8h9">
                      {on && <CheckIcon className="h-3 w-3 text-volt" strokeWidth={3} data-id="element-300" data-mp="3io6fo7okta53" />}
                    </span>
                  </span>
                  <span className="mt-2 line-clamp-2 block text-xs font-medium text-ink" data-id="element-301" data-mp="3nolp4o5qlxhs">{p.name}</span>
                  <span className="block text-sm font-bold text-ink" data-id="element-302" data-mp="39pcjgif5qls2">{formatPrice(p.price)}</span>
                </label>
              </React.Fragment>;
        })}
        </div>
        <div className="shrink-0 border-t border-line pt-5 lg:w-56 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0" data-id="element-303" data-mp="5auq82g89jg1">
          <p className="text-sm text-muted" data-id="element-304" data-mp="a8ed1qqzr4jl">Total for {1 + selectedExtras.length} items</p>
          <p className="mt-1 text-2xl font-bold tracking-tight text-ink" data-id="element-305" data-mp="3sm43wv6fr6xp">{formatPrice(total)}</p>
          <button type="button" onClick={addAll} className="mt-3 h-11 w-full rounded-lg bg-ink text-sm font-semibold text-white transition-colors duration-150 hover:bg-ink-soft" data-id="element-306" data-mp="hs249u7z278">
            Add {1 + selectedExtras.length} to cart
          </button>
        </div>
      </div>
    </section>;
}